import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { facultyApi } from '../../services';
import { useErrorHandler } from '../../lib/errorHandler';
import { FacultySchema } from '../../services/facultyApi';
import { z } from 'zod';
import { DIALOG_SETTINGS, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX } from '../../utils/responsive';
import { useResponsiveDialog, useEntityDialog, type DialogMode } from './hooks';
import { DialogFormActions, StatisticsPanel } from './base';

type Faculty = z.infer<typeof FacultySchema>;

interface FacultyDialogProps {
	open: boolean;
	onClose: () => void;
	faculty?: Faculty | null;
	mode: DialogMode;
}

export default function FacultyDialog({ open, onClose, faculty, mode }: FacultyDialogProps) {
	const { showError } = useErrorHandler();
	const { isMobile, isReadOnly } = useResponsiveDialog({ mode });

	const [formData, setFormData] = useState({
		maKhoa: '',
		tenKhoa: '',
		doUuTien: 0,
	});

	// Entity mutations
	const { isLoading, handleCreate, handleUpdate } = useEntityDialog<
		typeof formData,
		{ tenKhoa: string; doUuTien: number }
	>({
		entityName: 'khoa',
		queryKeys: [['faculties']],
		createFn: facultyApi.createFaculty,
		updateFn: (id, data) => facultyApi.updateFaculty(id, data),
		onSuccess: onClose,
	});

	// Fetch faculty statistics for view/edit mode
	const { data: facultyStatsData, isLoading: isLoadingStats } = useQuery({
		queryKey: ['faculty', 'stats', faculty?.maKhoa],
		queryFn: async () => {
			if (!faculty?.maKhoa) return null;
			const params = new URLSearchParams();
			params.set('maKhoa', faculty.maKhoa);
			params.set('limit', '1');
			const response = await facultyApi.getFacultyStats(params);
			return response.data?.stats?.[0] || null;
		},
		enabled: open && !!faculty?.maKhoa && (mode === 'view' || mode === 'edit'),
	});

	useEffect(() => {
		if ((mode === 'edit' || mode === 'view') && faculty) {
			setFormData({
				maKhoa: faculty.maKhoa || '',
				tenKhoa: faculty.tenKhoa || '',
				doUuTien: faculty.doUuTien || 0,
			});
		} else {
			setFormData({
				maKhoa: '',
				tenKhoa: '',
				doUuTien: 0,
			});
		}
	}, [faculty, mode, open]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isLoading || isReadOnly) return;

		if (!formData.maKhoa.trim() || !formData.tenKhoa.trim()) {
			showError('Vui lòng điền đầy đủ thông tin');
			return;
		}

		if (mode === 'create') {
			handleCreate(formData);
		} else if (mode === 'edit' && faculty) {
			handleUpdate(faculty.maKhoa, {
				tenKhoa: formData.tenKhoa,
				doUuTien: formData.doUuTien,
			});
		}
	};

	const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const getDialogTitle = () => {
		switch (mode) {
			case 'create':
				return 'Thêm khoa mới';
			case 'edit':
				return 'Chỉnh sửa khoa';
			case 'view':
				return 'Chi tiết khoa';
		}
	};

	// Compute year data and total for statistics
	const yearData = facultyStatsData?.yearData || null;
	const totalStudents = yearData
		? Object.values(yearData).reduce((sum: number, count) => sum + (count as number), 0)
		: 0;

	const renderDialogContent = () => (
		<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
				<TextField
					label="Mã khoa"
					value={formData.maKhoa}
					onChange={isReadOnly ? undefined : handleChange('maKhoa')}
					disabled={mode === 'edit' || isLoading}
					required={!isReadOnly}
					fullWidth
					slotProps={isReadOnly ? { input: { readOnly: true } } : undefined}
				/>
				<TextField
					label="Tên khoa"
					value={formData.tenKhoa}
					onChange={isReadOnly ? undefined : handleChange('tenKhoa')}
					disabled={isLoading}
					required={!isReadOnly}
					fullWidth
					slotProps={isReadOnly ? { input: { readOnly: true } } : undefined}
				/>

				{/* Statistics panel for view/edit mode */}
				{(mode === 'view' || mode === 'edit') && (
					<>
						{yearData && totalStudents > 0 && (
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
								<Chip
									label={`Tổng: ${totalStudents.toLocaleString()} sinh viên`}
									color="primary"
									size="small"
								/>
							</Box>
						)}
						<StatisticsPanel
							yearData={yearData}
							isLoading={isLoadingStats}
							title="Thống kê sinh viên theo năm"
							countLabel="Số sinh viên"
							showDivider={true}
						/>
					</>
				)}
			</Box>
		</DialogContent>
	);

	const renderDialogTitle = () => (
		<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
			{isMobile && (
				<IconButton
					aria-label="back"
					onClick={onClose}
					edge="start"
					disabled={isLoading}
					sx={{ mr: 1 }}
				>
					<ArrowBackIcon />
				</IconButton>
			)}
			{getDialogTitle()}
		</DialogTitle>
	);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={DIALOG_SETTINGS.FORM.MAX_WIDTH}
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				component: isReadOnly ? 'div' : 'form',
				onSubmit: isReadOnly ? undefined : handleSubmit,
				sx: {
					...(isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING }),
				},
			}}
		>
			{renderDialogTitle()}
			{renderDialogContent()}
			<DialogFormActions
				mode={mode}
				isLoading={isLoading}
				isReadOnly={isReadOnly}
				isMobile={isMobile}
				onClose={onClose}
			/>
		</Dialog>
	);
}